import { Tag, Tooltip } from 'antd';
import classNames from 'classnames';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDelta, formatRate, type PreviewStatus } from '../../lib/widget-pages';
import styles from './widget-pages.module.scss';

const PREVIEW_STATUS_COLOR: Record<PreviewStatus, string | undefined> = {
  edited: 'blue',
  auto: undefined,
  none: undefined,
  static: undefined,
};

type PreviewStatusTagProps = {
  status: PreviewStatus;
};

export const PreviewStatusTag: FC<PreviewStatusTagProps> = ({ status }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'analytics.widget.pages' });
  const tag = (
    <Tag
      bordered={false}
      color={PREVIEW_STATUS_COLOR[status]}
      className={classNames(styles.statusTag, {
        [styles.statusTagMuted]: status === 'none' || status === 'static',
      })}
    >
      {t(`previewStatus.${status}`)}
    </Tag>
  );

  if (status !== 'static') return tag;
  return <Tooltip title={t('previewStaticHint')}>{tag}</Tooltip>;
};

type DeltaBadgeProps = {
  delta: number | null;
  tooltip?: string;
};

export const DeltaBadge: FC<DeltaBadgeProps> = ({ delta, tooltip }) => {
  const text = formatDelta(delta);
  if (!text) return null;
  const rounded = Math.round(delta ?? 0);
  const badge = (
    <span
      className={classNames(styles.delta, {
        [styles.deltaUp]: rounded > 0,
        [styles.deltaDown]: rounded < 0,
      })}
    >
      {text}
    </span>
  );
  return tooltip ? <Tooltip title={tooltip}>{badge}</Tooltip> : badge;
};

type RateCellProps = {
  rate: number;
  count: number;
};

/** A conversion with its absolute count next to it, so a high percent on tiny traffic is visible. */
export const RateCell: FC<RateCellProps> = ({ rate, count }) => (
  <span className={styles.rateCell}>
    <span className={styles.value}>{formatRate(rate)}</span>
    <span className={styles.secondary}>{count}</span>
  </span>
);
